import { useState } from "react";
import { useOutletContext, useSearchParams } from "react-router";
import { isModuleSlug, SCHRIJVEN_TASK_LABELS, type ModuleSlug, type SchrijvenTaskType } from "@a2/core";
import type { SessionUser } from "../lib/session-loader";
import type { SessionMode } from "../components/welcome/PrepProgress";
import { useStartSpreken, useStartSchrijven, type SchrijvenMode } from "../lib/exam-start";
import { ModuleChipRow } from "../components/oefenen/ModuleChipRow";
import { SprekenModeBody } from "../components/oefenen/SprekenModeBody";
import { SchrijvenModeBody } from "../components/oefenen/SchrijvenModeBody";
import { BeginBar } from "../components/oefenen/BeginBar";
import { useT } from "../lib/use-t";

/** ?module= → user default → "spreken". Only "spreken"/"schrijven" have a body;
 *  any other (locked) slug falls back to spreken. */
function resolveInitialModule(param: string | null, s: SessionUser): ModuleSlug {
  const candidate = param && isModuleSlug(param) ? param : s.defaultModule;
  if (candidate === "schrijven") return "schrijven";
  return "spreken";
}

const SPREKEN_LABEL_KEY: Record<SessionMode, string> = {
  full: "modePicker.fullExamTitle",
  "deel-1": "modePicker.drill.deel1Label",
  "deel-2": "modePicker.drill.deel2Label",
  "deel-3": "modePicker.drill.deel3Label",
  "deel-4": "modePicker.drill.deel4Label",
};

export function OefenenPage() {
  const s = useOutletContext<SessionUser>();
  const t = useT();
  const [params] = useSearchParams();

  const [module, setModule] = useState<ModuleSlug>(() => resolveInitialModule(params.get("module"), s));
  const [sprekenMode, setSprekenMode] = useState<SessionMode>("full");
  const [schrijvenMode, setSchrijvenMode] = useState<SchrijvenMode>("exam");

  const spreken = useStartSpreken(t("modePicker.loadError"));
  const schrijven = useStartSchrijven(t("oefenen.networkError"), t("oefenen.unknownError"));

  const isSpreken = module === "spreken";
  const pending = isSpreken ? spreken.pending : schrijven.pending;
  const error = isSpreken ? spreken.error : schrijven.error;

  const selectedLabel = isSpreken
    ? t(SPREKEN_LABEL_KEY[sprekenMode])
    : schrijvenMode === "exam"
      ? t("oefenen.schrijven.heroTitle")
      : SCHRIJVEN_TASK_LABELS[schrijvenMode as SchrijvenTaskType];
  const durationLabel = isSpreken ? t(`oefenen.spreken.duration.${sprekenMode}`) : undefined;

  function onBegin() {
    if (isSpreken) {
      void spreken.start(sprekenMode);
    } else {
      void schrijven.start(schrijvenMode);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1000px] px-5 sm:px-8 pt-6 sm:pt-9 pb-2 space-y-6">
      <header>
        <p className="font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)]">
          {t("oefenen.eyebrow")}
        </p>
        <h1 className="mt-1.5 text-2xl sm:text-[32px] font-semibold tracking-tight">
          <span className="hidden sm:inline">{t("oefenen.title")}</span>
          <span className="sm:hidden">{t("oefenen.titleMobile")}</span>
        </h1>
        <p className="hidden sm:block mt-2 max-w-[56ch] text-[15px] text-[var(--ink-muted)]">
          {t("oefenen.intro")}
        </p>
      </header>

      <ModuleChipRow active={module} statusMap={s.moduleStatusMap} onPick={setModule} disabled={pending} />

      {isSpreken ? (
        <SprekenModeBody
          selected={sprekenMode}
          onSelect={setSprekenMode}
          disabled={pending}
          pending={spreken.pending}
          done={spreken.done}
        />
      ) : (
        <SchrijvenModeBody selected={schrijvenMode} onSelect={setSchrijvenMode} disabled={pending} />
      )}

      {error ? (
        <p role="alert" className="text-sm text-[var(--fail)]">
          {error}
        </p>
      ) : null}

      <BeginBar
        selectedLabel={selectedLabel}
        durationLabel={durationLabel}
        pending={pending}
        onBegin={onBegin}
        ctaLabel={t("oefenen.begin")}
        ctaLabelShort={t("oefenen.beginShort")}
      />
    </div>
  );
}
